/* Fuel Report — full-screen page behind the fuel card.
 *
 * The old page was four grey boxes, a paragraph of definitions in 12px grey,
 * and a five-column table. Fuel is one number moving over time, so this leads
 * with the buses that are silent, and puts the selected bus's efficiency next
 * to the rest of the fleet — "3.1 km/L" means nothing until you can see that
 * the fleet averages 3.7.
 *
 * HONESTY NOTES:
 *  · Efficiency is only shown when the bus has BOTH distance and logged litres.
 *    A bus with no refills gets a dash, not a zero — the missing figure is the
 *    finding, and a zero would read as "perfectly efficient".
 *  · Per-refill history (litres, price, station) lives on the bus page, not
 *    here: this component is given per-bus totals only.
 *  · Acknowledged buses are shown apart and never counted as problems. The
 *    owner already made that call; re-counting them is what made Zana look
 *    like she never finished a job.
 */

import React, { useMemo, useState } from 'react';
import { FaArrowLeft, FaGasPump, FaSearch, FaCheck, FaExclamationTriangle, FaChartBar } from 'react-icons/fa';
import { relativeTime, monogram } from '../../fleet/alertMeta';
import { useLockBodyScroll } from '../../fleet/useLockBodyScroll';

export interface FuelBus {
  id: string;
  name: string;
  plate: string;
  status: string;
  drivers: string[];
  fuelTankCapacityL?: number | null;
  benchmarkKmPerL?: number | null;
}

export interface FuelRow {
  busId: string;
  busName: string;
  km: number;
  needsRefuel: number;
  refuelEvents: number;
  hasRefueled: boolean;
  acked: boolean;
  litresAdded: number;
}

/** One driver “Start a refuel trip” run (busAttendance with routeType refuel). */
export interface FuelRefuelTrip {
  id: string;
  busId: string;
  busName: string;
  plate?: string;
  started: Date | null;
  finished: Date | null;
  km: number | null;
  visitedStation: boolean;
  loggedRefuel: boolean;
  /** Station stops inside the trip window / how many logged a fill (app-stamped
      from 2026-08-22). Older trips carry null and fall back to the booleans. */
  stationVisits?: number | null;
  refuelsLogged?: number | null;
  driverName?: string | null;
}

/**
 * A LOGGED FILL INSIDE THE RUN'S OWN WINDOW MEANS THE RUN VISITED A PUMP.
 *
 * `visitedStation` / `loggedRefuel` are worked out by the DRIVER APP when the
 * driver presses Finish. When the driver forgets and the server closes the run
 * instead (refuelTripSweep, ~6 h), nobody computes them at all — the record
 * keeps its defaults and the run reads "station not confirmed" for ever, no
 * matter what happened at the pump.
 *
 * Gabriel drove exactly that on 2026-08-30: geofence fired at the station, he
 * logged 6.9 L / KES 1,500, never ended the run, and the Fuel Report told him
 * the station was never confirmed.
 *
 * The backend fix stamps those fields properly from now on. This is the client
 * half, and it is the half that repairs runs ALREADY on the screen: if a fill
 * was logged for this bus between the run starting and finishing, the run
 * plainly reached a pump. Deliberately narrow — `lastRefuelAtByBusId` holds
 * only the MOST RECENT fill per bus, so this rescues the latest affected run
 * and quietly says nothing about older ones rather than guessing.
 */
function fillLandedInsideRun(t: FuelRefuelTrip, lastRefuelAtMs?: number): boolean {
  if (!lastRefuelAtMs || !t.started) return false;
  const from = t.started.getTime();
  const to = (t.finished ?? new Date()).getTime();
  return lastRefuelAtMs >= from && lastRefuelAtMs <= to;
}

/** "Fill logged" on a one-station run; "1 of 2 stations logged" when the bus
    stopped at more than one pump (Gabriel, 2026-08-22, refuel drive). */
function stationFillLabel(t: FuelRefuelTrip, lastRefuelAtMs?: number): string {
  const visits = t.stationVisits ?? 0;
  if (visits >= 2) return `${t.refuelsLogged ?? 0} of ${visits} stations logged`;
  if (t.loggedRefuel) return 'Fill logged';
  if (t.visitedStation || visits === 1) return 'Visited · no fill';
  // Says what we can prove, and says why the run itself does not show it.
  if (fillLandedInsideRun(t, lastRefuelAtMs)) return 'Fill logged · not stamped on the run';
  return 'Not confirmed';
}

export interface FuelReportScreenProps {
  rows: FuelRow[];
  busById: Record<string, FuelBus | undefined>;
  /** busId → epoch ms of the most recent logged refuel (0 when never). */
  lastRefuelAtByBusId: Record<string, number>;
  /** busId → 30-day fuel cost estimate in KES. */
  costByBusId: Record<string, number>;
  /** Finished (and in-progress) refuel trips — 4th pill, not mixed into school runs. */
  refuelTrips?: FuelRefuelTrip[];
  onClose: () => void;
  onOpenBus: (busId: string) => void;
  onOpenFuelPrices?: () => void;
}

type Band = 'silent' | 'thirsty' | 'flagged' | 'acked' | 'healthy';
type FuelFilter = 'attention' | 'silent' | 'all' | 'refuelTrips';

const BAND_LABEL: Record<Band, string> = {
  silent: 'No refill logged · 30 days',
  thirsty: 'Burning more than the fleet',
  flagged: 'Trips flagged, refill logged',
  acked: 'Parked by you',
  healthy: 'Healthy',
};

function efficiency(r: FuelRow): number | null {
  if (!(r.km > 0) || !(r.litresAdded > 0)) return null;
  return r.km / r.litresAdded;
}

export default function FuelReportScreen({
  rows, busById, lastRefuelAtByBusId, costByBusId, refuelTrips = [],
  onClose, onOpenBus, onOpenFuelPrices,
}: FuelReportScreenProps) {
  useLockBodyScroll();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FuelFilter>('attention');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);

  const fleetKmPerL = useMemo(() => {
    const km = rows.reduce((s, r) => s + (r.litresAdded > 0 ? r.km : 0), 0);
    const l = rows.reduce((s, r) => s + r.litresAdded, 0);
    return l > 0 ? km / l : null;
  }, [rows]);

  const bandOf = React.useCallback((r: FuelRow): Band => {
    if (r.acked) return 'acked';
    if (!r.hasRefueled) return 'silent';
    const e = efficiency(r);
    if (fleetKmPerL && e && e < fleetKmPerL * 0.85) return 'thirsty';
    if (r.needsRefuel > 0) return 'flagged';
    return 'healthy';
  }, [fleetKmPerL]);

  const totals = useMemo(() => {
    const totalKm = rows.reduce((s, r) => s + r.km, 0);
    const totalLitres = rows.reduce((s, r) => s + r.litresAdded, 0);
    const totalRefuels = rows.reduce((s, r) => s + r.refuelEvents, 0);
    const logging = rows.filter((r) => r.hasRefueled).length;
    const silent = rows.filter((r) => !r.hasRefueled && !r.acked).length;
    const acked = rows.filter((r) => !r.hasRefueled && r.acked).length;
    const spend = rows.reduce((s, r) => s + (costByBusId[r.busId] ?? 0), 0);
    return { totalKm, totalLitres, totalRefuels, logging, silent, acked, spend };
  }, [rows, costByBusId]);

  const ordered = useMemo(() => {
    const rank: Record<Band, number> = { silent: 0, thirsty: 1, flagged: 2, healthy: 3, acked: 4 };
    return [...rows].sort((a, b) => rank[bandOf(a)] - rank[bandOf(b)] || b.km - a.km);
  }, [rows, bandOf]);

  const visible = useMemo(() => {
    if (filter === 'refuelTrips') return [];
    const q = search.trim().toLowerCase();
    return ordered.filter((r) => {
      const band = bandOf(r);
      if (filter === 'silent' && band !== 'silent') return false;
      if (filter === 'attention' && (band === 'healthy' || band === 'acked')) return false;
      if (!q) return true;
      const b = busById[r.busId];
      return (
        r.busName.toLowerCase().includes(q) ||
        (b?.plate ?? '').toLowerCase().includes(q) ||
        (b?.drivers ?? []).join(' ').toLowerCase().includes(q)
      );
    });
  }, [ordered, filter, search, busById, bandOf]);

  const visibleTrips = useMemo(() => {
    if (filter !== 'refuelTrips') return [];
    const q = search.trim().toLowerCase();
    return refuelTrips.filter((t) => {
      if (!q) return true;
      return (
        t.busName.toLowerCase().includes(q) ||
        (t.plate ?? '').toLowerCase().includes(q) ||
        (t.driverName ?? '').toLowerCase().includes(q)
      );
    });
  }, [filter, refuelTrips, search]);

  const selected = useMemo(
    () => visible.find((r) => r.busId === selectedId) ?? visible[0] ?? ordered[0] ?? null,
    [visible, selectedId, ordered],
  );

  const selectedTrip = useMemo(
    () => visibleTrips.find((t) => t.id === selectedTripId) ?? visibleTrips[0] ?? null,
    [visibleTrips, selectedTripId],
  );

  const bus = selected ? busById[selected.busId] : undefined;
  const band = selected ? bandOf(selected) : 'healthy';
  const eff = selected ? efficiency(selected) : null;
  const lastRefuel = selected ? lastRefuelAtByBusId[selected.busId] ?? 0 : 0;
  const cost = selected ? costByBusId[selected.busId] ?? 0 : 0;

  /* Efficiency comparison: every bus that HAS a figure, plus the fleet average
     as its own row, plus the selected bus even when it has none — a dash in the
     ranking is the most honest way to show "this one cannot be measured". */
  const effBars = useMemo(() => {
    const withEff = rows
      .map((r) => ({ busId: r.busId, name: r.busName, e: efficiency(r) }))
      .filter((x) => x.e != null) as { busId: string; name: string; e: number }[];
    withEff.sort((a, b) => b.e - a.e);
    const out: { key: string; label: string; value: number | null; kind: 'bus' | 'avg' | 'self' }[] =
      withEff.slice(0, 6).map((x) => ({
        key: x.busId,
        label: x.name,
        value: x.e,
        kind: x.busId === selected?.busId ? 'self' : 'bus',
      }));
    if (fleetKmPerL) out.push({ key: '__avg', label: 'Fleet average', value: fleetKmPerL, kind: 'avg' });
    if (selected && efficiency(selected) == null) {
      out.push({ key: '__self', label: selected.busName, value: null, kind: 'self' });
    }
    out.sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
    return out;
  }, [rows, fleetKmPerL, selected]);

  const maxEff = effBars.reduce((m, x) => Math.max(m, x.value ?? 0), 0) || 1;

  const grouped = useMemo(() => {
    const out: { label: string; items: FuelRow[] }[] = [];
    for (const r of visible) {
      const label = BAND_LABEL[bandOf(r)];
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(r);
      else out.push({ label, items: [r] });
    }
    return out;
  }, [visible, bandOf]);

  return (
    /* `data-zt` is a takeover spotlight hook. The fuel walkthrough could not
       glow anything on this screen because this screen had no hook — and it
       had no hook because no walkthrough ever reached it (see LENSES.fuel). */
    <div className="fd-screen" data-zt="fuelReport">
      <header className="fd-appbar">
        <button type="button" className="fd-iconbtn" onClick={onClose} aria-label="Back">
          <FaArrowLeft />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>Fuel Report</h1>
          <p className="fd-sub">
            {rows.length} bus{rows.length === 1 ? '' : 'es'} · last 30 days
            {totals.silent > 0 ? ` · ${totals.silent} logging nothing` : ' · all logging refills'}
          </p>
        </div>
        {onOpenFuelPrices && (
          <button type="button" className="fd-btn fd-btn-quiet fd-btn-sm" onClick={onOpenFuelPrices}>
            Fuel prices
          </button>
        )}
      </header>

      <section className="fd-strip" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="fd-cell">
          <span className="fd-kicker">Distance covered</span>
          <div className="fd-big fd-num">{Math.round(totals.totalKm).toLocaleString()}<small>km</small></div>
          <p className="fd-cellsub">All buses, every finished run · last 30 days</p>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Buses logging refills</span>
          <div className="fd-big fd-num">{totals.logging}<small>of {rows.length}</small></div>
          {rows.length > 0 && (
            <>
              <div className="fd-sevbar">
                <i style={{ width: `${(totals.logging / rows.length) * 100}%`, background: 'var(--fd-ok-600)' }} />
                <i style={{ width: `${(totals.silent / rows.length) * 100}%`, background: 'var(--fd-crit-600)' }} />
                <i style={{ width: `${(totals.acked / rows.length) * 100}%`, background: 'var(--fd-ink-400)' }} />
              </div>
              <div className="fd-sevlegend">
                <span><span className="fd-dot fd-dot-ok" />{totals.logging} logging</span>
                {totals.silent > 0 && <span><span className="fd-dot fd-dot-crit" />{totals.silent} silent</span>}
                {totals.acked > 0 && <span><span className="fd-dot" style={{ background: 'var(--fd-ink-400)' }} />{totals.acked} parked</span>}
              </div>
            </>
          )}
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Recorded going in</span>
          <div className="fd-big fd-num">{Math.round(totals.totalLitres).toLocaleString()}<small>litres</small></div>
          <p className="fd-cellsub">{totals.totalRefuels} refill{totals.totalRefuels === 1 ? '' : 's'} logged by drivers</p>
        </div>

        {/* `data-zt` — the takeover glows this tile when it narrates the
            30-day fuel total, which lives here rather than on the dashboard. */}
        <div className="fd-cell" data-zt="fuelSpend">
          <span className="fd-kicker">Estimated spend</span>
          <div className="fd-big fd-num">{totals.spend > 0 ? Math.round(totals.spend).toLocaleString() : '—'}<small>KES</small></div>
          <p className="fd-cellsub">
            <span className="fd-pill fd-pill-warn">Estimate</span>{' '}
            Litres valued at the price on file, not what the pump charged.
          </p>
        </div>
      </section>

      <div className="fd-split">
        <aside className="fd-rail">
          <div className="fd-railtop">
            <div className="fd-search">
              <FaSearch style={{ color: 'var(--fd-ink-400)', flex: '0 0 auto' }} />
              <input
                type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bus, plate or driver" aria-label="Search buses"
              />
            </div>
            <div className="fd-chips">
              {([
                ['attention', `Needs a look · ${rows.filter((r) => { const b = bandOf(r); return b !== 'healthy' && b !== 'acked'; }).length}`],
                ['silent', `No refill · ${totals.silent}`],
                ['all', `All buses · ${rows.length}`],
                ['refuelTrips', `Refuel trips · ${refuelTrips.length}`],
              ] as const).map(([key, label]) => (
                <button
                  key={key} type="button" className="fd-chip"
                  aria-pressed={filter === key}
                  onClick={() => {
                    setFilter(key);
                    setSelectedId(null);
                    setSelectedTripId(null);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="fd-raillist">
            {filter === 'refuelTrips' ? (
              <>
                {visibleTrips.length === 0 && (
                  <div className="fd-empty">
                    <FaGasPump size={26} />
                    <b>No refuel trips yet</b>
                    <span>When a driver taps “Start a refuel trip”, the run lands here — not on school morning/evening lists.</span>
                  </div>
                )}
                {visibleTrips.length > 0 && (
                  <div>
                    <p className="fd-groupname">Driver refuel runs</p>
                    {visibleTrips.map((t) => {
                      const when = t.finished ?? t.started;
                      // Same rescue as the detail tile — a run the server
                      // closed still shows the fill the driver really logged.
                      const status = !t.finished
                        ? 'In progress'
                        : t.loggedRefuel
                          ? 'Fill logged'
                          : t.visitedStation
                            ? 'At station · no fill logged'
                            : fillLandedInsideRun(t, lastRefuelAtByBusId[t.busId])
                              ? 'Fill logged · not stamped on the run'
                              : 'Finished · station not confirmed';
                      return (
                        <button
                          key={t.id}
                          type="button"
                          className="fd-item"
                          aria-current={selectedTrip?.id === t.id ? 'true' : undefined}
                          onClick={() => setSelectedTripId(t.id)}
                        >
                          <span className={`fd-ig ${t.loggedRefuel ? 'fd-ig-ok' : 'fd-ig-warn'}`}>
                            <FaGasPump />
                          </span>
                          <span className="fd-itxt">
                            <span className="fd-ititle" style={{ display: 'block' }}>{t.busName}</span>
                            <span className="fd-imeta">
                              <span>{t.plate || t.busId}</span>
                              <span className="fd-sep">·</span>
                              <span>{t.km != null && t.km > 0 ? `${Math.round(t.km)} km` : '—'}</span>
                              <span className="fd-sep">·</span>
                              <span>{when ? relativeTime(when) : '—'}</span>
                            </span>
                            <span className="fd-imeta" style={{ display: 'block', marginTop: 2 }}>{status}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                {visible.length === 0 && (
                  <div className="fd-empty">
                    <FaCheck size={26} />
                    <b>Nothing to chase</b>
                    <span>Every bus has logged a refill in the last 30 days.</span>
                  </div>
                )}
                {grouped.map((g) => (
                  <div key={g.label}>
                    <p className="fd-groupname">{g.label}</p>
                    {g.items.map((r) => {
                      const b = busById[r.busId];
                      const bnd = bandOf(r);
                      const e = efficiency(r);
                      const ig = bnd === 'silent' ? 'fd-ig-crit'
                        : bnd === 'thirsty' || bnd === 'flagged' ? 'fd-ig-warn'
                        : bnd === 'acked' ? 'fd-ig-mute' : 'fd-ig-ok';
                      return (
                        <button
                          key={r.busId} type="button" className={`fd-item${bnd === 'acked' ? ' fd-item-read' : ''}`}
                          aria-current={selected?.busId === r.busId ? 'true' : undefined}
                          onClick={() => setSelectedId(r.busId)}
                        >
                          <span className={`fd-ig ${ig}`}>
                            {bnd === 'silent' ? <FaExclamationTriangle /> : bnd === 'thirsty' ? <FaChartBar /> : <FaGasPump />}
                          </span>
                          <span className="fd-itxt">
                            <span className="fd-ititle" style={{ display: 'block' }}>{r.busName}</span>
                            <span className="fd-imeta">
                              <span>{b?.plate || r.busId}</span>
                              <span className="fd-sep">·</span>
                              <span>{Math.round(r.km)} km</span>
                              <span className="fd-sep">·</span>
                              <span>{e ? `${e.toFixed(1)} km/L` : `${r.refuelEvents} refill${r.refuelEvents === 1 ? '' : 's'}`}</span>
                            </span>
                          </span>
                          {bnd === 'silent' && <span className="fd-unread" />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
        </aside>

        <section className="fd-detail">
          {filter === 'refuelTrips' ? (
            <>
              {!selectedTrip && (
                <div className="fd-empty" style={{ paddingTop: 120 }}>
                  <b>No refuel trips</b>
                  <span>Drivers start these from the bus home screen when they go to fill up.</span>
                </div>
              )}
              {selectedTrip && (
                <>
                  <div className="fd-hero">
                    <div className="fd-heroinner">
                      <div style={{ minWidth: 0 }}>
                        <span className="fd-pill fd-pill-onbrand">Refuel trip</span>
                        <h2>
                          {(selectedTrip.stationVisits ?? 0) >= 2
                            ? `Fill logged at ${selectedTrip.refuelsLogged ?? 0} of ${selectedTrip.stationVisits} stations on this run`
                            : selectedTrip.loggedRefuel
                            ? 'Fill was logged on this run'
                            : selectedTrip.visitedStation
                              ? 'Reached a station — fill not logged yet'
                              : selectedTrip.finished
                                ? 'Run finished without a confirmed station visit'
                                : 'Driver is still on this refuel run'}
                        </h2>
                        <div className="fd-herometa">
                          <span className="fd-avatar">{monogram(selectedTrip.busName)}</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>{selectedTrip.busName}</span>
                          {selectedTrip.plate && <span className="fd-plate">{selectedTrip.plate}</span>}
                          {selectedTrip.driverName && (<><span>·</span><span>{selectedTrip.driverName}</span></>)}
                          <span>·</span>
                          <span>
                            {(selectedTrip.finished ?? selectedTrip.started)
                              ? relativeTime(selectedTrip.finished ?? selectedTrip.started!)
                              : 'No time stamped'}
                          </span>
                        </div>
                      </div>
                      <div className="fd-heroactions">
                        <button type="button" className="fd-btn fd-btn-white fd-btn-sm" onClick={() => onOpenBus(selectedTrip.busId)}>
                          Open bus
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="fd-tiles">
                    <div className="fd-card fd-tile">
                      <span className="fd-kicker">Distance</span>
                      <p className="fd-tileval fd-num">
                        {selectedTrip.km != null && selectedTrip.km > 0
                          ? `${Math.round(selectedTrip.km).toLocaleString()} km`
                          : '—'}
                      </p>
                      <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>GPS when the run finishes</p>
                    </div>
                    <div className="fd-card fd-tile">
                      <span className="fd-kicker">Started</span>
                      <p className="fd-tileval" style={{ fontSize: 18 }}>
                        {selectedTrip.started
                          ? selectedTrip.started.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                          : '—'}
                      </p>
                    </div>
                    <div className="fd-card fd-tile">
                      <span className="fd-kicker">Finished</span>
                      <p className="fd-tileval" style={{ fontSize: 18 }}>
                        {selectedTrip.finished
                          ? selectedTrip.finished.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                          : 'In progress'}
                      </p>
                    </div>
                    <div className="fd-card fd-tile">
                      <span className="fd-kicker">Station / fill</span>
                      <p className="fd-tileval" style={{ fontSize: 18 }}>
                        {stationFillLabel(selectedTrip, lastRefuelAtByBusId[selectedTrip.busId])}
                      </p>
                    </div>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Do next</h3>
                    <button type="button" className="fd-btn fd-btn-primary fd-btn-sm fd-btn-block" onClick={() => onOpenBus(selectedTrip.busId)}>
                      Open bus page
                    </button>
                    <p className="fd-tiny fd-muted" style={{ margin: '12px 0 0' }}>
                      Refuel trips are separate from school morning and evening runs. Fill history (litres, price)
                      still lives on the bus page under logged refuels.
                    </p>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
          {!selected && (
            <div className="fd-empty" style={{ paddingTop: 120 }}>
              <b>No buses in this fleet</b>
              <span>Add a bus and its fuel figures will appear here.</span>
            </div>
          )}

          {selected && (
            <>
              <div className="fd-hero">
                <div className="fd-heroinner">
                  <div style={{ minWidth: 0 }}>
                    <span className="fd-pill fd-pill-onbrand">{BAND_LABEL[band]}</span>
                    <h2>
                      {band === 'silent'
                        ? `${Math.round(selected.km).toLocaleString()} km driven, nothing recorded going in`
                        : band === 'thirsty'
                          ? `Using more fuel per kilometre than the rest of the fleet`
                          : band === 'flagged'
                            ? `${selected.needsRefuel} trip${selected.needsRefuel === 1 ? '' : 's'} flagged low, refills are being logged`
                            : band === 'acked'
                              ? `You parked this one — it is not being counted`
                              : `${selected.refuelEvents} refill${selected.refuelEvents === 1 ? '' : 's'} logged over ${Math.round(selected.km).toLocaleString()} km`}
                    </h2>
                    <div className="fd-herometa">
                      <span className="fd-avatar">{monogram(selected.busName)}</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{selected.busName}</span>
                      {bus?.plate && <span className="fd-plate">{bus.plate}</span>}
                      {bus?.drivers?.[0] && (<><span>·</span><span>{bus.drivers[0]}</span></>)}
                      <span>·</span>
                      <span>{lastRefuel > 0 ? `Last refill ${relativeTime(new Date(lastRefuel))}` : 'No refill ever logged'}</span>
                    </div>
                  </div>
                  <div className="fd-heroactions">
                    <button type="button" className="fd-btn fd-btn-white fd-btn-sm" onClick={() => onOpenBus(selected.busId)}>
                      Open bus
                    </button>
                  </div>
                </div>
              </div>

              <div className="fd-tiles">
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Distance · 30 d</span>
                  <p className="fd-tileval fd-num">{Math.round(selected.km).toLocaleString()} km</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>From finished routes</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Litres logged</span>
                  <p className="fd-tileval fd-num">
                    {selected.litresAdded > 0 ? Math.round(selected.litresAdded).toLocaleString() : 0}
                    {selected.litresAdded === 0 && <span className="fd-tiledelta fd-up">▲ 30 d</span>}
                  </p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {selected.refuelEvents} refill{selected.refuelEvents === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Fuel cost · 30 d</span>
                  <p className="fd-tileval fd-num">{cost > 0 ? Math.round(cost).toLocaleString() : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>KES, estimated</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Efficiency</span>
                  <p className="fd-tileval fd-num">{eff ? eff.toFixed(1) : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {eff
                      ? `km/L${fleetKmPerL ? ` · fleet ${fleetKmPerL.toFixed(1)}` : ''}`
                      : 'Needs distance and logged litres'}
                  </p>
                </div>
              </div>

              <div className="fd-grid">
                <div>
                  <div className="fd-card fd-block">
                    <h3>Kilometres per litre<span className="fd-rest">Last 30 days</span></h3>
                    {effBars.length === 0 ? (
                      <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                        No bus in this fleet has both distance and logged litres yet, so nothing can be compared.
                      </p>
                    ) : (
                      <div className="fd-bars">
                        {effBars.map((x) => (
                          <div className="fd-bar" key={x.key}>
                            <span className="fd-lbl" style={x.kind === 'self' ? { color: 'var(--fd-brand-700)' } : undefined}>
                              {x.label}
                            </span>
                            <span className="fd-trk">
                              <i
                                className={`fd-fil ${x.value == null ? 'fd-fil-crit' : x.kind === 'avg' ? 'fd-fil-mute' : x.kind === 'self' ? '' : 'fd-fil-ok'}`}
                                style={{ width: x.value == null ? '0%' : `${(x.value / maxEff) * 100}%` }}
                              />
                            </span>
                            <span className="fd-val fd-num">{x.value == null ? '—' : x.value.toFixed(1)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {eff == null && (
                      <p className="fd-tiny fd-muted" style={{ margin: '12px 0 0' }}>
                        This bus has no figure at all — that is the point. A bus with no refills cannot be
                        compared, so it cannot be caught by an efficiency check either.
                      </p>
                    )}
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>How this bus is doing</h3>
                    <div className="fd-bcrow" style={{ borderTop: 0 }}>
                      <span>Trips flagged &ldquo;needs refuel&rdquo;</span>
                      <span className={selected.needsRefuel > 0 ? 'fd-num' : 'fd-num fd-muted'}>{selected.needsRefuel}</span>
                    </div>
                    <div className="fd-bcrow"><span>Refills logged · 30 d</span><span className="fd-num">{selected.refuelEvents}</span></div>
                    <div className="fd-bcrow"><span>Litres recorded</span><span className="fd-num">{Math.round(selected.litresAdded).toLocaleString()}</span></div>
                    <div className="fd-bcrow">
                      <span>Last refill</span>
                      <span>{lastRefuel > 0 ? new Date(lastRefuel).toLocaleDateString() : <span className="fd-nodata">Never</span>}</span>
                    </div>
                    <div className="fd-bcrow">
                      <span>Tank capacity</span>
                      <span>{bus?.fuelTankCapacityL ? `${bus.fuelTankCapacityL} L` : <span className="fd-nodata">Not set</span>}</span>
                    </div>

                    <div className="fd-explain">
                      <span className="fd-zbadge">Z</span>
                      <div>
                        <b>Zana · what this means</b>
                        <p>
                          {band === 'silent'
                            ? `${Math.round(selected.km).toLocaleString()} km with nothing recorded going in. Either the driver has stopped logging, or fuel is leaving the tank another way — and until a refill is logged, no efficiency check can tell you which.`
                            : band === 'thirsty'
                              ? `This bus is using noticeably more fuel per kilometre than the fleet average${fleetKmPerL ? ` of ${fleetKmPerL.toFixed(1)} km/L` : ''}. That usually points at the vehicle or the route rather than the price of fuel.`
                              : band === 'acked'
                                ? 'You parked this bus, so it is shown but never counted as a problem. It will resurface on its own if a fresh dry spell starts after a later refill.'
                                : 'Refills are being logged against real distance, which is what makes every other fuel check on this bus trustworthy.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="fd-card fd-buscard">
                    <div className="fd-bctop">
                      <span className="fd-avatar" style={{ borderColor: 'rgba(255,255,255,.5)' }}>{monogram(selected.busName)}</span>
                      <div>
                        <b>{selected.busName}</b>
                        <span>{bus?.plate ?? ''}{bus?.drivers?.[0] ? ` · ${bus.drivers[0]}` : ''}</span>
                      </div>
                    </div>
                    <div className="fd-bcinner">
                      <div className="fd-bcrow">
                        <span>Status</span>
                        <span>
                          <span className={`fd-pill ${bus?.status === 'active' ? 'fd-pill-ok' : 'fd-pill-warn'}`}>
                            <span className={`fd-dot ${bus?.status === 'active' ? 'fd-dot-ok' : 'fd-dot-warn'}`} />
                            {bus?.status === 'active' ? 'In service'
                              : bus?.status === 'for_hire' ? 'For hire'
                              : bus?.status === 'suspended' ? 'Suspended' : 'Maintenance'}
                          </span>
                        </span>
                      </div>
                      <div className="fd-bcrow">
                        <span>Benchmark</span>
                        <span>{bus?.benchmarkKmPerL ? `${bus.benchmarkKmPerL} km/L` : <span className="fd-nodata">Not set</span>}</span>
                      </div>
                      <div className="fd-bcrow"><span>Distance · 30 d</span><span className="fd-num">{Math.round(selected.km).toLocaleString()} km</span></div>
                      <div className="fd-bcrow"><span>Efficiency</span><span className="fd-num">{eff ? `${eff.toFixed(1)} km/L` : '—'}</span></div>
                    </div>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Do next</h3>
                    <button type="button" className="fd-btn fd-btn-primary fd-btn-sm fd-btn-block" onClick={() => onOpenBus(selected.busId)}>
                      Open bus page
                    </button>
                    {onOpenFuelPrices && (
                      <button type="button" className="fd-btn fd-btn-quiet fd-btn-sm fd-btn-block" onClick={onOpenFuelPrices}>
                        Set the fuel price
                      </button>
                    )}
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>What these mean</h3>
                    <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                      <strong>Trips flagged</strong> — post-trip surveys where the driver said empty, low, or
                      &ldquo;needs refuel soon&rdquo;. <strong>Refills logged</strong> — times a driver actually
                      recorded a fill. <strong>Distance</strong> — GPS distance when a route finishes.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
